import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems } from '../utils/loadSplit';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2568">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/50 dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#555555] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'}`} data-id="element-2569">
        <span className="truncate" data-id="element-2570">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2571" />
      </button>

      <AnimatePresence data-id="element-2572">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-2573">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2574">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2575" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2576">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2577" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2578">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2579">
          <ChevronLeft className="w-5 h-5" data-id="element-2580" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2581">
          <ChevronRight className="w-5 h-5" data-id="element-2582" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2583">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2584" />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState<string>('12');
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [shape, setShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState('');
  const [calcYards, setCalcYards] = useState(0);
  const [calcTons, setCalcTons] = useState(0);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  // Calculator Logic
  useEffect(() => {
    let areaSqFt = 0;
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const d = parseFloat(diameter) || 0;
    const b = parseFloat(base) || 0;
    const h = parseFloat(height) || 0;
    const dp = parseFloat(depth) || 0;
    const mToFt = 3.28084;
    const lFt = unit === 'm' ? l * mToFt : l;
    const wFt = unit === 'm' ? w * mToFt : w;
    const dFt = unit === 'm' ? d * mToFt : d;
    const bFt = unit === 'm' ? b * mToFt : b;
    const hFt = unit === 'm' ? h * mToFt : h;
    if (shape === 'rectangle' || shape === 'irregular') {
      areaSqFt = lFt * wFt;
    } else if (shape === 'circle') {
      const radius = dFt / 2;
      areaSqFt = Math.PI * radius * radius;
    } else if (shape === 'triangle') {
      areaSqFt = 0.5 * bFt * hFt;
    }
    const dpFt = depthUnit === 'cm' ? dp / 30.48 : dp / 12;
    const cubicFeet = areaSqFt * dpFt;
    const cubicYards = cubicFeet / 27;
    setCalcYards(Math.max(0, cubicYards));
    if (product) {
      const pn = product.name.toLowerCase();
      let density = 1.3;
      if (pn.includes('bark') || pn.includes('play chips')) density = 0.45;else if (pn.includes('compost')) density = 0.6;else if (pn.includes('sand')) density = 1.4;else if (pn.includes('topsoil') || pn.includes('50/50')) density = 1.1;
      setCalcTons(Math.max(0, cubicYards * density));
    }
  }, [shape, length, width, diameter, base, height, depth, unit, depthUnit, product]);
  const handleUseQuantity = () => {
    const roundedYards = Math.ceil(calcYards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('fill dirt') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: just pre-select the dropdown value. The 5yd minimum is
      // enforced by the cart's billing math, so we display the actual yards
      // the user asked for (e.g. 6yd shows as 6, not 5).
      const minSingle = light ? 7 : 6;
      const snapped = Math.max(minSingle, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      setAddedFlash(true);
      setTimeout(() => setAddedFlash(false), 2000);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    if (isNaN(n) || n < 5) return;
    const pn = product?.name.toLowerCase() || '';
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('fill dirt') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const minSingle = light ? 7 : 6;
      const snapped = Math.max(minSingle, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2585">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2586">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2587">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('50/50') || pn.includes('fill dirt') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 2000);
  };
  // Handle multi-load custom quote redirect
  useEffect(() => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      setNumLoads('');
      setQuantity('12');
    }
  }, [quantity, numLoads, isLight]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2588">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2589">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2590">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2591">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2592" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2593">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2594">
                <ChevronLeft className="w-4 h-4" data-id="element-2595" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2596">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2597">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2598">
                <ChevronLeft className="w-4 h-4" data-id="element-2599" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2600">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2601">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2602">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2603" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2604">
                <ChevronRight className="w-4 h-4" data-id="element-2605" />
              </span>}
          </div>
        </div>

        <div className="bg-[#faf8f6] dark:bg-[#333333] rounded-2xl border border-stone-200 dark:border-[#444444] overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-2606">
          {/* Image */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative" data-id="element-2607">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2608" />
          </div>

          {/* Content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col" data-id="element-2609">
            <div className="mb-2" data-id="element-2610">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2611">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2612">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2613">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2614">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2615" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2616">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2617" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2618">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2619" />}
              </button>
            </div>

            <div className="mb-8 min-h-[200px]" data-id="element-2620">
              <AnimatePresence mode="wait" data-id="element-2621">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2622">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2623">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2624">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2625">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2626">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2627">
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="bg-brand-orange/10 border border-brand-orange/20 p-4 rounded-xl flex items-start gap-3" data-id="element-2628">
                        <div className="bg-brand-orange/20 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2629">
                          <Check className="w-4 h-4 text-brand-orange" data-id="element-2630" />
                        </div>
                        <div data-id="element-2631">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2632">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2633">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2634">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2635">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2636">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2637">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2638">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2639">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2640">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2641">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2642">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2643">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2644" />
                        </div>
                        <div className="min-w-0" data-id="element-2645">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2646">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2647">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2648">
                        <Download className="w-4 h-4" data-id="element-2649" />
                        <span className="hidden sm:inline" data-id="element-2650">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2651">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2652">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2653" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2654">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2655">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2656">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@253gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2657">
                            info@253gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="mt-auto" data-id="element-2658">
              {isSpecialty ? <div className="space-y-4" data-id="element-2659">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2660">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2661">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2662">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2663" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-6" data-id="element-2664">
                  <div className="flex items-end justify-between border-b border-stone-100 dark:border-[#444444] pb-6" data-id="element-2665">
                    <div className="w-full" data-id="element-2666">
                      <p className="text-sm text-brand-muted dark:text-stone-500 font-medium mb-1" data-id="element-2667">
                        Price
                      </p>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const smallLoadThreshold = isLight ? 12 : 10;
                    const MIN = 3;
                    // Estimator handoff drives totalYards when active so
                    // the bulk-discount and savings preview reflect the
                    // exact estimator yardage (e.g. 14, not 24).
                    const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '3+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                    // Split into per-load yardages so we can apply the
                    // small-load surcharge ONLY to a partial last load
                    // (matches cart billing exactly).
                    const loads: number[] = [];
                    if (requestedYards > 0) {
                      if (requestedYards <= fullLoad) {
                        loads.push(Math.max(MIN, requestedYards));
                      } else {
                        const count = Math.ceil(requestedYards / fullLoad);
                        const last = Math.max(MIN, requestedYards - (count - 1) * fullLoad);
                        for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                        loads.push(last);
                      }
                    }
                    const billableYards = loads.reduce((a, b) => a + b, 0);
                    const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                    // Compute per-load cost: each load gets its own
                    // small-load surcharge if below threshold, then the
                    // whole order gets the bulk discount if eligible.
                    let totalCost = 0;
                    let hasSurchargedLoad = false;
                    for (const ld of loads) {
                      let sur = 1;
                      if (ld < smallLoadThreshold) {
                        if (ld >= 5) {
                          sur = 1 + (smallLoadThreshold - ld) * 0.1;
                        } else {
                          const at5 = 1 + (smallLoadThreshold - 5) * 0.1;
                          sur = at5 + (5 - ld) * 0.15;
                        }
                      }
                      if (sur > 1) hasSurchargedLoad = true;
                      totalCost += Math.ceil(product.pricePerYard * sur) * ld;
                    }
                    if (bulkPct > 0) {
                      totalCost = Math.round(totalCost * (1 - bulkPct));
                    }
                    const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                    const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                    return <>
                            <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2668">
                              $
                              {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                              <span className="text-base font-normal text-brand-muted dark:text-stone-500" data-id="element-2669">
                                / yard{showsEffective ? ' avg' : ''}
                              </span>
                              {bulkPct > 0 && <span className="inline-flex items-center text-xs font-bold text-stone-900 bg-amber-300 dark:bg-amber-400 px-2 py-0.5 rounded-md ml-2 align-middle uppercase tracking-wide" data-id="element-2670">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span>}
                            </p>

                            {/* Effective-price breakdown: explains why the
                             shown $/yd may differ from the base $/yd —
                             small-load surcharges on partial loads and
                             bulk discounts both shift the average. */}
                            {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 leading-relaxed" data-id="element-2671">
                                  <span className="line-through" data-id="element-2672">
                                    ${product.pricePerYard}/yd
                                  </span>
                                  <span className="text-amber-700 dark:text-amber-400 ml-2 font-semibold" data-id="element-2673">
                                    small-load surcharge applies
                                  </span>
                                  <span className="block mt-0.5" data-id="element-2674">
                                    Base ${product.pricePerYard}/yd at{' '}
                                    {smallLoadThreshold}+ yards per delivery.
                                  </span>
                                </p>}
                            {showsEffective && bulkPct > 0 && <p className="text-sm text-brand-muted dark:text-stone-500 mt-1" data-id="element-2675">
                                <span className="line-through" data-id="element-2676">
                                  ${product.pricePerYard}/yd
                                </span>
                                <span className="text-amber-600 dark:text-amber-400 ml-2 font-semibold" data-id="element-2677">
                                  Save $
                                  {product.pricePerYard * billableYards - totalCost}{' '}
                                  on {billableYards} yards
                                </span>
                              </p>}
                            {/* No-surcharge state: clarify the threshold so
                             customers know when the base price applies. */}
                            {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-2678">
                                Base price at {smallLoadThreshold}+ yards per
                                delivery. Smaller loads include a surcharge.
                              </p>}
                            {/* Live order-total preview when we know the
                             quantity — gives buyers an honest "this is
                             what you'll pay for the material" number. */}
                            {billableYards > 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-2" data-id="element-2679">
                                Materials subtotal for {billableYards} yd:{' '}
                                <span className="font-semibold text-brand-charcoal dark:text-white" data-id="element-2680">
                                  ${totalCost.toLocaleString()}
                                </span>{' '}
                                <span className="opacity-70" data-id="element-2681">
                                  (delivery extra)
                                </span>
                              </p>}
                          </>;
                  })()}
                    </div>
                  </div>

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#1DB954]/30 bg-[#1DB954]/10 p-3 sm:p-4" data-id="element-2682">
                          <div className="flex items-start gap-3" data-id="element-2683">
                            <div className="w-8 h-8 rounded-full bg-[#1DB954]/20 flex items-center justify-center shrink-0" data-id="element-2684">
                              <Check className="w-4 h-4 text-[#1DB954]" data-id="element-2685" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2686">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-2687">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-2688">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-2689">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-2690">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-4" data-id="element-2691">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-2692">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2693">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-2694">
                            5% at {tier2Yards} yd ·{' '}
                            <span className="font-semibold text-[#AEB9B2] dark:text-[#6FB389]" data-id="element-2695">
                              10% at {tier3Yards} yd
                            </span>
                          </span>;
                  })()}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4" data-id="element-2696">
                      <div className="w-full sm:w-1/2" data-id="element-2697">
                        {/* Single dropdown — Multiple loads on top, Full Load in the #2 slot, then smaller yardages 11→3 below. Selecting "Multiple loads" reveals the load-count dropdown below. */}
                        {(() => {
                      const fullLoad = isLight ? 15 : 12;
                      const lessYards = [11, 10, 9, 8, 7, 6, 5, 4, 3];
                      const quantityOptions = [{
                        value: 'multi',
                        label: `Multiple loads (${fullLoad * 2}+ yd)`
                      }, {
                        value: String(fullLoad),
                        label: `Full Load — ${fullLoad} yd · Most picked`
                      }, ...lessYards.map(y => ({
                        value: String(y),
                        label: y === 3 ? `${y} yd · Order minimum` : `${y} yd`
                      }))];
                      return <CustomSelect value={quantity} onChange={val => {
                        setQuantity(val);
                        if (val !== 'multi') setNumLoads('');
                      }} placeholder="Select quantity" options={quantityOptions} data-id="element-2698" />;
                    })()}

                        <AnimatePresence data-id="element-2699">
                          {quantity === 'multi' && <motion.div initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} exit={{
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-visible mt-3" data-id="element-2700">
                              <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                          value: '2',
                          label: `2 Loads (${isLight ? '30' : '24'} yds)`
                        }, {
                          value: '3',
                          label: `3 Loads (${isLight ? '45' : '36'} yds)`
                        }, {
                          value: '4',
                          label: `4 Loads (${isLight ? '60' : '48'} yds)`
                        }, {
                          value: '3+',
                          label: '5+ Loads (Custom Quote)'
                        }]} data-id="element-2701" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="flex-1 flex items-center justify-center bg-[#76cc26]/10 dark:bg-[#76cc26]/10 text-[#76cc26] dark:text-[#76cc26] rounded-none font-bold border border-[#76cc26]/30 dark:border-[#76cc26]/30 py-3 sm:py-0" data-id="element-2702">
                          <Check className="w-5 h-5 mr-2" data-id="element-2703" />
                          Added to Truck
                        </motion.div> : <button onClick={handleAddToCart} disabled={quantity === 'multi' && !numLoads} className={`flex-1 py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${quantity && quantity !== 'multi' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : quantity === 'multi' && numLoads && numLoads !== '3+' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : 'bg-brand-charcoal dark:bg-white/10 text-white dark:text-white hover:bg-brand-charcoal/90 dark:hover:bg-white/15 border border-transparent dark:border-white/10'}`} data-id="element-2704">
                          <Truck className="w-5 h-5 mr-2" data-id="element-2705" />
                          Add to Truck
                        </button>}
                    </div>

                    <div className="flex justify-center" data-id="element-2706">
                      <SameDayBadge variant="full" data-id="element-2707" />
                    </div>

                    <AnimatePresence data-id="element-2708">
                      {quantity === 'multi' && numLoads && numLoads !== '3+' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-2709">
                            <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-2710">
                              <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2711" />
                              <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-2712">
                                Multi-load orders require separate deliveries.
                                You will be charged a delivery fee per load.
                              </p>
                            </div>
                          </motion.div>}
                    </AnimatePresence>

                    {quantity === 'multi' && !numLoads && <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-2713">
                        <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2714" />
                        <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-2715">
                          Please select how many loads you need.
                        </p>
                      </div>}
                  </div>

                  {quantity === 'multi' && numLoads === '3+' && <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-none font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2716">
                      Get a Custom Quote
                    </button>}

                  {/* Custom amount / bulk note */}
                  <p className="text-xs font-medium text-brand-charcoal/70 dark:text-white/70 text-center" data-id="element-2717">
                    Need a custom amount?{' '}
                    <a href="tel:206-619-3742" className="text-brand-orange hover:underline" data-id="element-2718">
                      Call us
                    </a>{' '}
                    or{' '}
                    <Link to="/contact" className="text-brand-orange hover:underline" data-id="element-2719">
                      contact us
                    </Link>
                    .
                  </p>

                  {/* Helpful resource links — pill row anchored to bottom of card */}
                  <div className="flex flex-wrap items-center justify-center gap-2 pt-3 mt-1 border-t border-brand-charcoal/10 dark:border-white/10" data-id="element-2720">
                    <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-2721">
                      Compare Materials
                    </Link>
                    <Link to={`/help?from=product&slug=${encodeURIComponent(product.slug)}`} className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-2722">
                      Quiz & Calculator
                    </Link>
                    <Link to="/diy-help" className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-2723">
                      DIY Help
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-2724" />

      {/* Calculator Drawer */}
      <div className="fixed top-24 right-0 z-40 pointer-events-none flex items-start" data-id="element-2725">
        {/* Panel */}
        <motion.div initial={{
        x: 360
      }} animate={{
        x: isCalcOpen ? 0 : 360
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 30
      }} className="bg-white dark:bg-[#222] border border-r-0 border-stone-200 dark:border-[#444] shadow-2xl w-[320px] sm:w-[360px] h-[calc(100vh-120px)] rounded-l-2xl flex flex-col pointer-events-auto overflow-hidden" data-id="element-2726">
          {/* Header */}
          <div className="p-4 border-b border-stone-200 dark:border-[#333] flex items-center justify-between shrink-0 bg-stone-50 dark:bg-[#1a1a1a]" data-id="element-2727">
            <h2 className="font-bold flex items-center text-brand-charcoal dark:text-white text-sm" data-id="element-2728">
              <CalcIcon className="w-4 h-4 mr-2 text-brand-orange" data-id="element-2729" />
              Coverage Calculator
            </h2>
            <button onClick={() => setIsCalcOpen(false)} className="p-1.5 hover:bg-stone-200 dark:hover:bg-[#333] rounded-md transition-colors" data-id="element-2730">
              <X className="w-4 h-4 text-stone-500" data-id="element-2731" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-2732">
            {/* Shape Tabs */}
            <div className="bg-stone-100 dark:bg-[#1a1a1a] p-1 rounded-xl flex gap-1" data-id="element-2733">
              {(['rectangle', 'circle', 'triangle', 'irregular'] as const).map(s => <button key={s} onClick={() => setShape(s)} className={`flex-1 py-1.5 px-1 text-[10px] font-bold rounded-lg transition-colors capitalize ${shape === s ? 'bg-white dark:bg-[#333] text-brand-charcoal dark:text-white shadow-sm' : 'text-stone-500 hover:text-stone-700 dark:hover:text-stone-300'}`} data-id="element-2734">
                    {s === 'irregular' ? 'Irreg.' : s}
                  </button>)}
            </div>

            {/* Inputs */}
            <div className="space-y-4" data-id="element-2735">
              <div className="flex justify-end" data-id="element-2736">
                <div className="flex bg-stone-100 dark:bg-[#1a1a1a] rounded-lg p-1" data-id="element-2737">
                  <button onClick={() => setUnit('ft')} className={`px-2 py-1 text-[10px] font-bold rounded-md ${unit === 'ft' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-2738">
                    Feet
                  </button>
                  <button onClick={() => setUnit('m')} className={`px-2 py-1 text-[10px] font-bold rounded-md ${unit === 'm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-2739">
                    Meters
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-2740">
                {(shape === 'rectangle' || shape === 'irregular') && <>
                    <div data-id="element-2741">
                      <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1.5" data-id="element-2742">
                        Length ({unit})
                      </label>
                      <input type="number" value={length} onChange={e => setLength(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" placeholder="0" data-id="element-2743" />
                    </div>
                    <div data-id="element-2744">
                      <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1.5" data-id="element-2745">
                        Width ({unit})
                      </label>
                      <input type="number" value={width} onChange={e => setWidth(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" placeholder="0" data-id="element-2746" />
                    </div>
                  </>}
                {shape === 'circle' && <div className="col-span-2" data-id="element-2747">
                    <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1.5" data-id="element-2748">
                      Diameter ({unit})
                    </label>
                    <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" placeholder="0" data-id="element-2749" />
                  </div>}
                {shape === 'triangle' && <>
                    <div data-id="element-2750">
                      <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1.5" data-id="element-2751">
                        Base ({unit})
                      </label>
                      <input type="number" value={base} onChange={e => setBase(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" placeholder="0" data-id="element-2752" />
                    </div>
                    <div data-id="element-2753">
                      <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1.5" data-id="element-2754">
                        Height ({unit})
                      </label>
                      <input type="number" value={height} onChange={e => setHeight(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" placeholder="0" data-id="element-2755" />
                    </div>
                  </>}
              </div>

              <div className="border-t border-stone-100 dark:border-[#333] pt-4 mt-2" data-id="element-2756">
                <div className="flex justify-between items-end mb-1.5" data-id="element-2757">
                  <label className="block text-[10px] font-bold text-stone-500 uppercase tracking-wider" data-id="element-2758">
                    Depth
                  </label>
                  <div className="flex bg-stone-100 dark:bg-[#1a1a1a] rounded-lg p-1" data-id="element-2759">
                    <button onClick={() => setDepthUnit('in')} className={`px-2 py-1 text-[10px] font-bold rounded-md ${depthUnit === 'in' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-2760">
                      Inches
                    </button>
                    <button onClick={() => setDepthUnit('cm')} className={`px-2 py-1 text-[10px] font-bold rounded-md ${depthUnit === 'cm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-2761">
                      cm
                    </button>
                  </div>
                </div>
                <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" placeholder="0" data-id="element-2762" />
              </div>
            </div>
          </div>

          {/* Footer / Results */}
          <div className="p-4 border-t border-stone-200 dark:border-[#333] bg-stone-50 dark:bg-[#1a1a1a] shrink-0" data-id="element-2763">
            <div className="flex items-center justify-between mb-4" data-id="element-2764">
              <div data-id="element-2765">
                <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-0.5" data-id="element-2766">
                  Est. Yards
                </p>
                <p className="text-2xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2767">
                  {calcYards > 0 ? calcYards.toFixed(1) : '0.0'}
                </p>
              </div>
              <div className="text-right" data-id="element-2768">
                <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-0.5" data-id="element-2769">
                  Est. Tons
                </p>
                <p className="text-lg font-bold text-stone-400" data-id="element-2770">
                  {calcTons > 0 ? calcTons.toFixed(1) : '0.0'}
                </p>
              </div>
            </div>
            <button onClick={handleUseQuantity} disabled={calcYards === 0} className="w-full py-3 bg-brand-orange hover:bg-brand-orange-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors flex items-center justify-center" data-id="element-2771">
              <Check className="w-4 h-4 mr-2" data-id="element-2772" />
              Use {Math.ceil(calcYards)}{' '}
              {Math.ceil(calcYards) === 1 ? 'Yard' : 'Yards'}
            </button>
          </div>
        </motion.div>

        {/* Tab Handle */}
        <motion.button initial={{
        x: 0
      }} animate={{
        x: isCalcOpen ? 360 : 0
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 30
      }} onClick={() => setIsCalcOpen(!isCalcOpen)} className="absolute right-0 top-10 bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 transition-colors rounded-l-xl shadow-2xl py-4 px-2 flex flex-col items-center gap-2 pointer-events-auto border border-r-0 border-brand-charcoal dark:border-white" data-id="element-2773">
          <motion.div animate={{
          opacity: [0.5, 1, 0.5],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="text-brand-orange" data-id="element-2774">
            <CalcIcon className="w-4 h-4" data-id="element-2775" />
          </motion.div>
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)'
        }} data-id="element-2776">
            Calculate
          </span>
        </motion.button>
      </div>
    </motion.main>;
}